<template>
    <div>
        <!-- 파일 목록 -->
        <div v-if="!selectedFile">
            <table class="uk-table uk-table-small uk-table-striped uk-table-hover">
                <thead>
                    <tr>
                        <th>파일명</th>
                        <th style="width: 120px;">크기</th>
                        <th style="width: 180px;">최종 수정일</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="file in files"
                        :key="file.name"
                        style="cursor: pointer;"
                        @click="loadFile(file.name)">
                        <td>
                            <span :style="fileNameStyle(file.name)">{{ file.name }}</span>
                        </td>
                        <td>{{ formatSize(file.size) }}</td>
                        <td>{{ file.modified_at }}</td>
                    </tr>
                </tbody>
            </table>
            <div v-if="files.length === 0" class="uk-text-muted uk-text-center uk-padding-small">
                로그 파일이 없습니다.
            </div>
        </div>

        <!-- 상세 뷰 -->
        <div v-else>
            <!-- 헤더 -->
            <div class="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
                <div>
                    <a href="#" @click.prevent="goBack" class="uk-text-muted">&larr; 목록으로</a>
                    <span class="uk-margin-small-left uk-text-bold">{{ selectedFile }}</span>
                    <span v-if="totalEntries > 0" class="uk-badge uk-margin-small-left">{{ totalEntries }}건</span>
                </div>
                <div class="uk-flex uk-flex-middle">
                    <label class="uk-margin-small-right">
                        <input type="checkbox" class="uk-checkbox" v-model="showInfoLogs">
                        INFO 표시
                    </label>
                    <label class="uk-margin-small-right">
                        <input type="checkbox" class="uk-checkbox" v-model="hideVendorFrames">
                        vendor 프레임 숨기기
                    </label>
                </div>
            </div>

            <!-- 로딩 -->
            <div v-if="loading" class="uk-text-center uk-padding">
                <div uk-spinner></div>
                <div class="uk-text-muted uk-margin-small-top">로그를 불러오는 중...</div>
            </div>

            <!-- 에러 -->
            <div v-else-if="loadError" class="uk-alert-danger" uk-alert>
                {{ loadError }}
            </div>

            <!-- 엔트리 목록 -->
            <div v-else>
                <div v-if="filteredEntries.length === 0" class="uk-text-muted uk-text-center uk-padding-small">
                    표시할 로그 엔트리가 없습니다.
                </div>

                <div v-for="(entry, idx) in filteredEntries"
                     :key="idx"
                     class="uk-margin-small-bottom"
                     :style="entryBorderStyle(entry)">

                    <!-- 엔트리 헤더 -->
                    <div :style="entryHeaderStyle(entry)"
                         style="padding: 6px 10px; font-size: 1rem; cursor: pointer;"
                         @click="toggleEntry(idx)">
                        <span class="uk-text-bold" :style="{ color: levelColor(entry.level) }">
                            [{{ entry.level }}]
                        </span>
                        <span class="uk-text-muted uk-margin-small-left" style="font-size: 1rem;">
                            {{ entry.datetime }}
                        </span>
                        <span class="uk-margin-small-left" style="word-break: break-all;">
                            {{ truncate(entry.summary, 200) }}
                        </span>
                        <span v-if="entry.has_trace"
                              class="uk-badge uk-margin-small-left"
                              style="font-size: 1rem; background: #dc3545;">
                            Stack Trace
                        </span>
                    </div>

                    <!-- 엔트리 상세 (펼침) -->
                    <div v-if="expandedEntries[idx]"
                         style="padding: 8px 10px; border-top: 1px solid #e5e5e5; background: #fafafa;">

                        <!-- 메시지 전문 (트레이스 제외) -->
                        <div style="font-family: monospace; font-size: 1rem; white-space: pre-wrap; word-break: break-all; color: #333; padding: 6px; background: #fff; border: 1px solid #eee; border-radius: 3px;">{{ entry.summary }}</div>

                        <!-- 스택트레이스 -->
                        <div v-if="entry.has_trace" class="uk-margin-small-top">
                            <div class="uk-text-bold" style="font-size: 1rem; margin-bottom: 4px;">Stack Trace:</div>
                            <div style="font-family: monospace; font-size: 1rem; background: #1e1e1e; color: #d4d4d4; padding: 10px; border-radius: 3px; overflow-x: auto;">
                                <div v-for="(frame, fIdx) in visibleTrace(entry)"
                                     :key="fIdx"
                                     :style="traceFrameStyle(frame)"
                                     style="padding: 1px 0; line-height: 1.6;">
                                    <template v-if="frame.file">
                                        <span style="color: #6a9955;">#{{ fIdx }}</span>
                                        <span :style="{ color: frame.is_vendor ? '#808080' : '#569cd6' }">{{ shortenPath(frame.file) }}</span><span style="color: #d4d4d4;">:</span><span style="color: #b5cea8;">{{ frame.line }}</span>
                                        <span style="color: #dcdcaa;">{{ frame.call }}</span>
                                    </template>
                                    <template v-else>
                                        <span style="color: #6a9955;">{{ frame.raw }}</span>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            initFiles: { type: Array, default: function () { return []; } },
            csrfToken: { type: String, default: '' }
        },
        data: function () {
            return {
                files: this.initFiles,
                selectedFile: null,
                entries: [],
                totalEntries: 0,
                loading: false,
                loadError: null,
                expandedEntries: {},
                showInfoLogs: false,
                hideVendorFrames: true
            };
        },
        computed: {
            filteredEntries: function () {
                if (this.showInfoLogs) {
                    return this.entries;
                }
                return this.entries.filter(function (e) {
                    return e.level !== 'INFO';
                });
            }
        },
        methods: {
            fileNameStyle: function (name) {
                if (name.indexOf('fpm-fcgi') !== -1) {
                    return { color: '#1e87f0', fontWeight: 'bold' };
                }
                if (name.indexOf('cli') !== -1) {
                    return { color: '#32d296', fontWeight: 'bold' };
                }
                return {};
            },
            formatSize: function (bytes) {
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            },
            loadFile: function (filename) {
                var self = this;
                self.selectedFile = filename;
                self.loading = true;
                self.loadError = null;
                self.entries = [];
                self.expandedEntries = {};

                $.ajax({
                    url: '/system/error-log/' + encodeURIComponent(filename),
                    method: 'GET',
                    headers: { 'Accept': 'application/json' },
                    success: function (data) {
                        self.entries = data.entries;
                        self.totalEntries = data.total;
                        self.loading = false;
                    },
                    error: function (xhr) {
                        self.loadError = '로그 파일을 불러올 수 없습니다. (' + xhr.status + ')';
                        self.loading = false;
                    }
                });
            },
            goBack: function () {
                this.selectedFile = null;
                this.entries = [];
                this.expandedEntries = {};
            },
            toggleEntry: function (idx) {
                this.$set(this.expandedEntries, idx, !this.expandedEntries[idx]);
            },
            truncate: function (str, max) {
                if (!str) return '';
                if (str.length <= max) return str;
                return str.substring(0, max) + '...';
            },
            levelColor: function (level) {
                var colors = {
                    'EMERGENCY': '#721c24',
                    'ALERT': '#856404',
                    'CRITICAL': '#dc3545',
                    'ERROR': '#e3342f',
                    'WARNING': '#f6993f',
                    'NOTICE': '#6cb2eb',
                    'INFO': '#38c172',
                    'DEBUG': '#b8c2cc'
                };
                return colors[level] || '#333';
            },
            entryBorderStyle: function (entry) {
                var color = this.levelColor(entry.level);
                return {
                    border: '1px solid #e5e5e5',
                    borderLeft: '3px solid ' + color,
                    borderRadius: '3px'
                };
            },
            entryHeaderStyle: function (entry) {
                if (entry.level === 'ERROR' || entry.level === 'CRITICAL' || entry.level === 'EMERGENCY' || entry.level === 'ALERT') {
                    return { background: '#fff5f5' };
                }
                if (entry.level === 'WARNING') {
                    return { background: '#fffbeb' };
                }
                return { background: '#fff' };
            },
            visibleTrace: function (entry) {
                if (!this.hideVendorFrames) {
                    return entry.trace;
                }
                return entry.trace.filter(function (frame) {
                    return !frame.is_vendor;
                });
            },
            traceFrameStyle: function (frame) {
                if (frame.is_vendor) {
                    return { opacity: '0.5' };
                }
                return {};
            },
            shortenPath: function (path) {
                if (!path) return '';
                // /home/deployer/goyolo/ 제거
                return path.replace(/^\/home\/deployer\/goyolo\//, '');
            }
        }
    };
</script>
